import {type FishGroup, type Action, type FishTypeResponse} from 'client';
import {type CellActionType, type EventsRowData, FormSelectionEnum, LockedEnum, SupportedLanguages} from 'interfaces';
import {dateReader, kilosToGrams, kilosToTons, numberDisplay} from 'helpers/UnitsFormatHandler';
import {dateDisplay} from 'helpers/dateUtils';
import {naturalComparator} from 'helpers/naturalSort';
import {type EventsTableFilterRange, type EventsTableFilterState} from './eventsTableFilters';

const comparator = naturalComparator(SupportedLanguages.english);

export const getHeadCells = (withRegions = false, withFishGroupProperties = false) => {
  const headCells: HeadCell[] = [
    {id: 'date', numeric: false, label: 'date', sortKey: 'dateTime'},
    {id: 'actionType', numeric: false, label: 'actionType', sortKey: 'actionType'},
    {id: 'species', numeric: false, label: 'species', sortKey: 'species'},
    {id: 'siteName', numeric: false, label: 'site', sortKey: 'siteName'},
    {id: 'cageName', numeric: false, label: 'cage', sortKey: 'cageName'},
    {id: 'fishTypeName', numeric: false, label: 'fishTypeName', sortKey: 'fishTypeName'},
    {id: 'biomass', numeric: true, label: 'biomass', sortKey: 'biomassNumber', units: 'tonsAbbreviated'},
    {id: 'count', numeric: true, label: 'count', sortKey: 'countNumber'},
    {id: 'weight', numeric: true, label: 'weight', sortKey: 'weightNumber', units: 'gramsAbbreviated'},
    {id: 'isLocked', numeric: false, label: 'isLocked', sortKey: 'isLocked'},
    {id: 'certificates', numeric: false, label: 'certificates', sortKey: 'certificates'},
    ...(withFishGroupProperties
      ? [
          {
            id: 'fishGroupProperties',
            numeric: false,
            label: 'fishGroupProperties',
            sortKey: 'fishGroupProperties',
          } as const,
        ]
      : []),
    {id: 'actions', numeric: false, label: '', sortKey: 'actions', isSticky: true},
  ];

  if (withRegions) {
    headCells.splice(2, 0, {id: 'regionName', numeric: false, label: 'region', sortKey: 'regionName'});
  }

  return headCells;
};

export const actionsHandler = (
  rawActions: Action[],
  fishTypes: FishTypeResponse[],
  fishGroups: FishGroup[],
): EventsRowData[] => {
  const actions = rawActions.map((rawAction) => {
    const date = dateReader(rawAction.date);
    const biomass = kilosToTons(rawAction.biomass ?? 0);
    const count = rawAction.count ?? 0;
    const weight = kilosToGrams(rawAction.weight ?? 0);
    const fishType = fishTypes.find((fishType) => fishType.id === rawAction.fishTypeId);

    return {
      id: rawAction.id,
      date: dateDisplay(date),
      dateTime: date.getTime(),
      cellType: actionTypeHandler(rawAction),
      actionType: actionTypeHandlerReplace(rawAction),
      actionTypeRaw: rawAction.actionType,
      siteName: rawAction.siteName,
      siteId: rawAction.siteId,
      regionId: rawAction.regionId,
      regionName: rawAction.regionName,
      cageName: rawAction.cageName,
      cageId: rawAction.cageId,
      isLocked: rawAction.isLocked ?? false,
      fullHarvest: rawAction.fullHarvest,
      fishTypeName: fishType?.name ?? '',
      species: rawAction.species,
      certificates: rawAction.certificates?.sort((a, b) => comparator(a, b)).join('|'),
      biomass: numberDisplay(biomass, 0),
      biomassNumber: biomass,
      count: numberDisplay(count, 0),
      countNumber: count,
      weight: numberDisplay(weight, 0),
      weightNumber: weight,
      actions: 'actions',
      fromFishGroupId: rawAction.fromFishGroupId,
      toFishGroupId: rawAction.toFishGroupId,
      explanations: rawAction.explanations,
      fishGroupProperties: getFishGroupProperties(rawAction, fishGroups),
    };
  });
  return actions;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export function stableSort(array: EventsRowData[], comparator: (a: EventsRowData, b: EventsRowData) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [EventsRowData, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export const getAllActions = (displayLiveFishTransactions: boolean) => [
  FormSelectionEnum.input,
  FormSelectionEnum.harvest,
  FormSelectionEnum.emergencyHarvest,
  FormSelectionEnum.delousing,
  FormSelectionEnum.transferIn,
  FormSelectionEnum.transferOut,
  ...(displayLiveFishTransactions ? [FormSelectionEnum.liveFishTransaction] : []),
];
export const lockedUnlocked = [LockedEnum.locked, LockedEnum.unLocked];
export interface HeadCell {
  id: keyof EventsRowData;
  sortKey: keyof EventsRowData;
  numeric: boolean;
  label: string;
  units?: string;
  isSticky?: boolean;
}

export const actionTypeHandler = (action: Action): CellActionType => {
  switch (action.actionType) {
    case 'input':
      return FormSelectionEnum.input;
    case 'delousing':
      return FormSelectionEnum.delousing;
    case 'transferIn':
      return FormSelectionEnum.transfer;
    case 'transferOut':
      return FormSelectionEnum.transfer;
    case 'liveFishTransaction':
      return FormSelectionEnum.liveFishTransaction;
    default:
      return action?.isEmergency === true ? FormSelectionEnum.emergencyHarvest : FormSelectionEnum.harvest;
  }
};

export const actionTypeHandlerReplace = (action: Action): FormSelectionEnum => {
  switch (action.actionType) {
    case 'input':
      return FormSelectionEnum.input;
    case 'delousing':
      return FormSelectionEnum.delousing;
    case 'transferIn':
      return FormSelectionEnum.transferIn;
    case 'transferOut':
      return FormSelectionEnum.transferOut;
    case 'liveFishTransaction':
      return FormSelectionEnum.liveFishTransaction;
    default:
      return action?.isEmergency === true ? FormSelectionEnum.emergencyHarvest : FormSelectionEnum.harvest;
  }
};

const isValueInRange = (value: number, selectedRange?: EventsTableFilterRange) => {
  if (!selectedRange) {
    return true;
  }
  if (selectedRange.lowest !== 0 && selectedRange.highest !== 0) {
    return value >= selectedRange.lowest && value <= selectedRange.highest;
  } else if (selectedRange.lowest !== 0) {
    return value >= selectedRange.lowest;
  } else if (selectedRange.highest !== 0) {
    return value <= selectedRange.highest;
  } else {
    return true;
  }
};

const isValueInList = <T>(value: T, list?: T[]) => {
  if (list && list.length > 0) {
    return list.includes(value);
  }
  return true;
};

const areListIntersecting = <T>(listA: T[], listB: T[]) => {
  return listA.every((value) => listB.includes(value));
};

export const filterEventsTableActions = (
  rows: EventsRowData[],
  filters: EventsTableFilterState,
  config: {
    isRegionsEnabled: boolean;
  },
) => {
  const filteredRows = rows.filter((row) => {
    const areCertificatesIntersecting = () => {
      const rowCertificates = row.certificates ? row.certificates?.split('|') : [];
      if (filters.certificates && filters.certificates.length > 0) {
        return areListIntersecting(rowCertificates, filters.certificates);
      }
      return true;
    };

    const areFishGroupPropertiesIntersecting = () => {
      if (!filters.fishGroupProperties || filters.fishGroupProperties.length === 0) {
        return true;
      }

      return row.fishGroupProperties
        .map((property) => property.id)
        .some((rowProperty) => filters.fishGroupProperties?.includes(rowProperty));
    };

    const isAfterStartDate = () => {
      if (filters.startDate) {
        return row.dateTime >= filters.startDate.getTime();
      }
      return true;
    };

    const isBeforeEndDate = () => {
      if (filters.endDate) {
        return row.dateTime <= filters.endDate.getTime();
      }
      return true;
    };

    const areRegionsMatch = () => {
      if (config.isRegionsEnabled) {
        return isValueInList(row.regionName, filters.regions);
      }
      return true;
    };

    return (
      isAfterStartDate() &&
      isBeforeEndDate() &&
      isValueInList(row.actionType, filters.actionType) &&
      areRegionsMatch() &&
      isValueInList(row.siteName, filters.sites) &&
      isValueInList(row.cageName, filters.cages) &&
      isValueInList(row.species, filters.species) &&
      isValueInList(row.fishTypeName, filters.fishTypes) &&
      isValueInList(row.isLocked ? LockedEnum.locked : LockedEnum.unLocked, filters.locked) &&
      isValueInRange(row.biomassNumber, filters.biomasss) &&
      isValueInRange(row.countNumber, filters.count) &&
      isValueInRange(row.weightNumber, filters.weight) &&
      areCertificatesIntersecting() &&
      areFishGroupPropertiesIntersecting()
    );
  });

  return filteredRows;
};

export const getEventsTableSortComparator = (
  hookNaturalComarator: (x: string, y: string) => number,
  order: 'asc' | 'desc',
  orderBy: keyof EventsRowData,
): ((
  a: {[key in keyof EventsRowData]: EventsRowData[keyof EventsRowData]},
  b: {[key in keyof EventsRowData]: EventsRowData[keyof EventsRowData]},
) => number) => {
  return (a, b) => {
    const valueA = a[orderBy];
    const valueB = b[orderBy];
    let comparison = 0;
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      comparison = hookNaturalComarator(valueA, valueB);
    } else {
      comparison = descendingComparator(a, b, orderBy);
    }
    return order === 'desc' ? comparison : -comparison;
  };
};

const getFishGroupProperties = (action: Action, fishGroups: FishGroup[]) => {
  const fromFishGroup = fishGroups.find((fishGroup) => fishGroup.id === action.fromFishGroupId);
  const toFishGroup = fishGroups.find((fishGroup) => fishGroup.id === action.toFishGroupId);

  switch (action.actionType) {
    case 'input':
    case 'transferIn':
      return toFishGroup?.propertiesAttribute.value ?? [];
    default:
      return fromFishGroup?.propertiesAttribute.value ?? [];
  }
};
